import { isEditing } from "@builder.io/sdk-react";

import { useConfig } from "@jti/app-context/useConfig";
import { StandardProductDetailsContainer as UiStandardProductDetailsContainer } from "@jti/product-details/standard-product-details";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { useProductData } from "@/libs/commerce/ProductDataContext";

import type { StandardProductDetailsContainerProps } from "./StandardProductDetailsContainer.inputs.builder";

export function StandardProductDetailsContainer(props: StandardProductDetailsContainerProps) {
  const {
    DISABLE_COMMERCE,
    deviceRegistrationConfiguration,
    defaultProductCtaLabel,
    defaultProductCtaUrl,
    pdpConsentCard,
  } = useConfig();
  const { __ } = useTranslations();
  const productData = useProductData();

  return (
    <UiStandardProductDetailsContainer
      {...{
        ...props,
        isCommerceDisabled: !!DISABLE_COMMERCE,
        bundlesConfig: props.bundlesConfig ?? [],
        sku: props.sku ?? "",
        isTradeIn: props.isTradeIn ?? false,
        categories: props.categories ?? [],
        currentVariantId: props.currentVariantId ?? "",
        currentVariantSku: props.currentVariantSku ?? "",
        configurableOptions: props.configurableOptions ?? [],
        knowMoreUrl: props.knowMoreUrl ?? "",
        description: props.description ?? "",
        multiPackDescription: props.multiPackDescription ?? "",
        multiPackName: props.multiPackName ?? "",
        isAuthorMode: isEditing(),
        isBundle: props.isBundle ?? false,
        isTrial: props.isTrial ?? false,
        multiPackProducts: props.multiPackProducts ?? [],
        isMultiPackEnabled: props.isMultiPackEnabled ?? false,
        hideCta: props.hideCta ?? false,
        hidePrice: props.hidePrice ?? false,
        hasStrikethroughPrice: props.hasStrikethroughPrice ?? false,
        hasDiscountBadge: props.hasDiscountBadge ?? false,
        hasLoyaltyDiscountBadge: props.hasLoyaltyDiscountBadge ?? false,
        productName: props.productName ?? "",
        hasBadge: props.hasBadge ?? false,
        isDynamicGalleryEnabled: props.isDynamicGalleryEnabled ?? false,
        dynamicGallery: props.dynamicGallery ?? null,
        videoGallery: props.videoGallery ?? [],
        galleryAssets: props.galleryAssets ?? [],
        trustpilot: { isEnabled: props.trustpilot?.isEnabled ?? false },
        productCta: {
          label: props.productCta?.label || defaultProductCtaLabel || "",
          url: props.productCta?.url || defaultProductCtaUrl || "",
        },
        nonTradeInSku: props.nonTradeInSku ?? "",
        isTradeInDisclaimerVisible: props.isTradeInDisclaimerVisible ?? false,
        dynamicGalleryUrls: productData.dynamicGalleryUrls,
        multipacksQuantities: productData.multipacksQuantities,
        tradeInDisclaimerData: {
          title: props.tradeInDisclaimerData?.title ?? "",
          description: props.tradeInDisclaimerData?.description ?? "",
          ctaText: props.tradeInDisclaimerData?.ctaText ?? "",
          ctaLink: props.tradeInDisclaimerData?.ctaLink ?? "",
        },
        badgeText: __("product.badge.new"),
        overwriteBadgeText: props.overwriteBadgeText ?? "",
        loyaltyBadgeCopy: props.loyaltyDiscountBadgeCopy,
        loyaltyRewardCopy: props.loyaltyRewardCopy,
        isLoyaltyRewardCopyAuthoringEnabled: props.isLoyaltyRewardCopyAuthoringEnabled ?? false,
        // Prefer global config values; fallback to Builder inputs
        deviceRegistrationConfiguration: {
          theme: deviceRegistrationConfiguration?.theme ?? props.deviceRegistrationConfiguration?.theme ?? "light",
          registerDeviceImgMobile:
            deviceRegistrationConfiguration?.registerDeviceImgMobile ??
            props.deviceRegistrationConfiguration?.registerDeviceImgMobile ??
            "",
          registerDeviceAccordionButtonUrl:
            deviceRegistrationConfiguration?.registerDeviceAccordionButtonUrl ??
            props.deviceRegistrationConfiguration?.registerDeviceAccordionButtonUrl ??
            "",
          registerDeviceAccordionButtonText:
            deviceRegistrationConfiguration?.registerDeviceAccordionButtonText ??
            props.deviceRegistrationConfiguration?.registerDeviceAccordionButtonText ??
            "",
          registerDeviceImgDesktop:
            deviceRegistrationConfiguration?.registerDeviceImgDesktop ??
            props.deviceRegistrationConfiguration?.registerDeviceImgDesktop ??
            "",
          registerDeviceImgAlt:
            deviceRegistrationConfiguration?.registerDeviceImgAlt ??
            props.deviceRegistrationConfiguration?.registerDeviceImgAlt ??
            "",
          cancelDeviceRegistrationButtonText:
            deviceRegistrationConfiguration?.cancelDeviceRegistrationButtonText ??
            props.deviceRegistrationConfiguration?.cancelDeviceRegistrationButtonText ??
            "",
          registerDeviceDialogTitle:
            deviceRegistrationConfiguration?.registerDeviceDialogTitle ??
            props.deviceRegistrationConfiguration?.registerDeviceDialogTitle ??
            "",
          registerDeviceDialogDescription:
            deviceRegistrationConfiguration?.registerDeviceDialogDescription ??
            props.deviceRegistrationConfiguration?.registerDeviceDialogDescription ??
            "",
          registerDeviceAccordionTitle:
            deviceRegistrationConfiguration?.registerDeviceAccordionTitle ??
            props.deviceRegistrationConfiguration?.registerDeviceAccordionTitle ??
            "",
          confirmDeviceRegistrationButtonText:
            deviceRegistrationConfiguration?.confirmDeviceRegistrationButtonText ??
            props.deviceRegistrationConfiguration?.confirmDeviceRegistrationButtonText ??
            "",
          is12DeviceCodeLength:
            deviceRegistrationConfiguration?.is12DeviceCodeLength ??
            props.deviceRegistrationConfiguration?.is12DeviceCodeLength ??
            false,
          is15DeviceCodeLength:
            deviceRegistrationConfiguration?.is15DeviceCodeLength ??
            props.deviceRegistrationConfiguration?.is15DeviceCodeLength ??
            false,
          is16DeviceCodeLength:
            deviceRegistrationConfiguration?.is16DeviceCodeLength ??
            props.deviceRegistrationConfiguration?.is16DeviceCodeLength ??
            true,
          accordionImageOne:
            deviceRegistrationConfiguration?.accordionImageOne ??
            props.deviceRegistrationConfiguration?.accordionImageOne ??
            "",
          accordionImageOneAlt:
            deviceRegistrationConfiguration?.accordionImageOneAlt ??
            props.deviceRegistrationConfiguration?.accordionImageOneAlt ??
            "",
          accordionImageOneDescription:
            deviceRegistrationConfiguration?.accordionImageOneDescription ??
            props.deviceRegistrationConfiguration?.accordionImageOneDescription ??
            "",
          accordionImageTwo:
            deviceRegistrationConfiguration?.accordionImageTwo ??
            props.deviceRegistrationConfiguration?.accordionImageTwo ??
            "",
          accordionImageTwoAlt:
            deviceRegistrationConfiguration?.accordionImageTwoAlt ??
            props.deviceRegistrationConfiguration?.accordionImageTwoAlt ??
            "",
          accordionImageTwoDescription:
            deviceRegistrationConfiguration?.accordionImageTwoDescription ??
            props.deviceRegistrationConfiguration?.accordionImageTwoDescription ??
            "",
          isDeviceCodeFormatLabelVisible:
            deviceRegistrationConfiguration?.isDeviceCodeFormatLabelVisible ??
            props.deviceRegistrationConfiguration?.isDeviceCodeFormatLabelVisible ??
            true,
        },
        consentSectionConfiguration: pdpConsentCard ?? null,
        hasRegistrationConsent: props.hasRegistrationConsent,
        mandatoryPositiveConsent: productData.mandatoryPositiveConsent,
      }}
    />
  );
}
